import { point, lineString, lineSliceAlong, length as turfLength, bearing as turfBearing, destination, along } from "@turf/turf";
import {
  MIN_ARROW_LENGTH_M,
  MAX_ARROW_LENGTH_M,
  CONTINUATION_TOLERANCE_DEG,
  MAX_WAY_HOPS,
  MPH_TO_MPS,
  HEAD_SNAP_TOLERANCE_DEG,
  BLEND_FRACTION,
  MIN_BLEND_M,
  MAX_BLEND_M
} from "./constants";
import { bearingDifference, normalizeBearing, sameCoord } from "./geo";
import { allowedDirections } from "./matcher";

// pieces shorter than this are numerically meaningless and skipped
const MIN_PIECE_M = 0.5;
const METERS_PER_DEGREE_LAT = 111320;
const BLEND_SAMPLES = 12;

/**
 * Bearing for the arrowhead: the recorded heading (where the vehicle was pointing when the
 * event ended, e.g. part-way through a turn), snapped to the road bearing when they are
 * close enough that the difference is just GPS jitter.
 */
export function headBearing(heading, shaftBearing, snapTolerance = HEAD_SNAP_TOLERANCE_DEG) {
  if (!Number.isFinite(heading) || bearingDifference(heading, shaftBearing) < snapTolerance) {
    return normalizeBearing(shaftBearing);
  }
  return normalizeBearing(heading);
}

/**
 * Where the recorded heading diverges from the road, replace the last stretch of the
 * shaft with a cubic Bézier that leaves the road tangentially and arrives at the end
 * point tangent to the heading, so the arrow bends smoothly into the turn instead of
 * the head kinking away from a straight shaft.
 * @param {Array} coords tail -> head
 * @param {Number} heading recorded heading at the end of the event
 * @return {{coordinates: Array, headBearing: Number}}
 */
export function blendIntoHeading(coords, heading, { snapTolerance = HEAD_SNAP_TOLERANCE_DEG, blendFraction = BLEND_FRACTION, minBlend = MIN_BLEND_M, maxBlend = MAX_BLEND_M } = {}) {
  const n = coords.length;
  const shaftBearing = turfBearing(coords[n - 2], coords[n - 1]);
  const bearing = headBearing(heading, shaftBearing, snapTolerance);
  if (bearing === normalizeBearing(shaftBearing)) {
    return { coordinates: coords, headBearing: bearing };
  }

  const line = lineString(coords);
  const totalM = turfLength(line, { units: "meters" });
  const blendM = Math.min(totalM, maxBlend, Math.max(minBlend, totalM * blendFraction));
  const keepM = totalM - blendM;

  // straight part that stays on the road, and the tangent where the curve takes over
  const kept = keepM > MIN_PIECE_M ? lineSliceAlong(line, 0, keepM, { units: "meters" }).geometry.coordinates : [coords[0]];
  const start = kept[kept.length - 1];
  const probe = along(line, Math.min(totalM, keepM + 1), { units: "meters" }).geometry.coordinates;
  const startBearing = sameCoord(start, probe) ? shaftBearing : turfBearing(start, probe);

  // local metric frame (metres east/north of the end point)
  const end = coords[n - 1];
  const mPerDegLon = METERS_PER_DEGREE_LAT * Math.cos((end[1] * Math.PI) / 180);
  const toLocal = ([lon, lat]) => [(lon - end[0]) * mPerDegLon, (lat - end[1]) * METERS_PER_DEGREE_LAT];
  const fromLocal = ([x, y]) => [end[0] + x / mPerDegLon, end[1] + y / METERS_PER_DEGREE_LAT];
  const unit = (b) => [Math.sin((b * Math.PI) / 180), Math.cos((b * Math.PI) / 180)];

  const p0 = toLocal(start);
  const p3 = [0, 0];
  const handle = Math.hypot(p0[0], p0[1]) / 3;
  const [sx, sy] = unit(startBearing);
  const [hx, hy] = unit(bearing);
  const p1 = [p0[0] + sx * handle, p0[1] + sy * handle];
  const p2 = [p3[0] - hx * handle, p3[1] - hy * handle];

  const curve = [];
  for (let i = 1; i <= BLEND_SAMPLES; i++) {
    // concentrate samples towards the end so the final chord is tangent to the heading
    const t = 1 - (1 - i / BLEND_SAMPLES) ** 2;
    const mt = 1 - t;
    const x = mt ** 3 * p0[0] + 3 * mt ** 2 * t * p1[0] + 3 * mt * t ** 2 * p2[0] + t ** 3 * p3[0];
    const y = mt ** 3 * p0[1] + 3 * mt ** 2 * t * p1[1] + 3 * mt * t ** 2 * p2[1] + t ** 3 * p3[1];
    curve.push(fromLocal([x, y]));
  }
  curve[curve.length - 1] = end;

  return { coordinates: joinPieces([kept, curve]), headBearing: bearing };
}

/**
 * Arrow length (m) = distance travelled during the event, clamped.
 */
export function arrowLength(event, { minLength = MIN_ARROW_LENGTH_M, maxLength = MAX_ARROW_LENGTH_M } = {}) {
  const { start_speed_mph: speed, duration_s: duration } = event;
  if (!Number.isFinite(speed) || !Number.isFinite(duration)) {
    return minLength;
  }
  return Math.min(maxLength, Math.max(minLength, speed * MPH_TO_MPS * duration));
}

/** Coordinates of a way ordered in the direction of travel. */
function orient(way, direction) {
  return direction === 1 ? way.coords : [...way.coords].reverse();
}

function sliceAlong(coords, startM, endM) {
  if (endM - startM < MIN_PIECE_M) {
    return null;
  }
  return lineSliceAlong(lineString(coords), startM, endM, { units: "meters" }).geometry.coordinates;
}

function joinPieces(pieces) {
  const line = [];
  for (const piece of pieces) {
    for (const coord of piece) {
      if (!line.length || !sameCoord(line[line.length - 1], coord)) {
        line.push(coord);
      }
    }
  }
  return line;
}

/**
 * Pick the connected way to continue the arrow tail onto. We walk backwards
 * (against travel), so the candidate is taken as the stretch of road that
 * *arrives* at the junction in its travel direction (the junction may be an
 * interior vertex of the candidate) and it must leave the junction roughly
 * in line with the way we came from.
 * @return {Object|null} { coords, lengthM, diff } oriented in travel direction, ending at the junction
 */
function pickContinuation(junction, entryBearing, index, visited, tolerance) {
  let best = null;
  for (const { way, index: vertex } of index.connectionsAt(junction)) {
    if (visited.has(way.id)) {
      continue;
    }
    const last = way.coords.length - 1;
    const candidates = [];
    if (vertex > 0 && allowedDirections(way).includes(1)) {
      candidates.push(way.coords.slice(0, vertex + 1));
    }
    if (vertex < last && allowedDirections(way).includes(-1)) {
      candidates.push(way.coords.slice(vertex).reverse());
    }
    for (const coords of candidates) {
      const exitBearing = turfBearing(coords[coords.length - 2], coords[coords.length - 1]);
      const diff = bearingDifference(exitBearing, entryBearing);
      if (diff > tolerance) {
        continue;
      }
      if (!best || diff < best.diff) {
        best = { way, coords, diff };
      }
    }
  }
  if (best) {
    best.lengthM = turfLength(lineString(best.coords), { units: "meters" });
  }
  return best;
}

/**
 * Straight arrow ending at the event, pointing along the reported heading.
 */
export function straightArrow(event, lengthM) {
  if (!Number.isFinite(event.heading)) {
    return null;
  }
  const head = [event.lon, event.lat];
  const tail = destination(point(head), lengthM, normalizeBearing(event.heading + 180), { units: "meters" }).geometry.coordinates;
  return { coordinates: [tail, head], headBearing: normalizeBearing(event.heading), lengthM, matched: false };
}

/**
 * Build the arrow geometry for an event. The event location marks the END of
 * the event, so the arrow head sits on the snapped point and the tail extends
 * backwards along the road (hopping across OSM way splits as needed). The head
 * points along the recorded heading, the tail shows the road travelled.
 * @return {Object|null} { coordinates, headBearing, lengthM, matched }
 */
export function buildArrow(event, match, index, options = {}) {
  const { continuationTolerance = CONTINUATION_TOLERANCE_DEG, maxHops = MAX_WAY_HOPS } = options;
  const targetLength = arrowLength(event, options);
  if (!match) {
    return straightArrow(event, targetLength);
  }

  const { way, direction } = match;
  let coords = orient(way, direction);
  const alongM = direction === 1 ? match.location : way.lengthM - match.location;
  let remaining = targetLength;
  const pieces = [];
  const visited = new Set([way.id]);

  const startM = Math.max(0, alongM - remaining);
  const first = sliceAlong(coords, startM, alongM);
  if (first) {
    first[first.length - 1] = match.snapped;
    pieces.unshift(first);
  }
  remaining -= alongM - startM;

  let hops = 0;
  while (remaining > MIN_PIECE_M && hops < maxHops) {
    const entryBearing = turfBearing(coords[0], coords[1]);
    const next = pickContinuation(coords[0], entryBearing, index, visited, continuationTolerance);
    if (!next) {
      break;
    }
    coords = next.coords;
    visited.add(next.way.id);
    hops++;
    const take = Math.min(remaining, next.lengthM);
    const piece = sliceAlong(coords, next.lengthM - take, next.lengthM);
    if (piece) {
      pieces.unshift(piece);
    }
    remaining -= take;
  }

  const coordinates = joinPieces(pieces);
  if (coordinates.length < 2) {
    return straightArrow(event, targetLength);
  }
  const blended = blendIntoHeading(coordinates, event.heading, options);
  return {
    coordinates: blended.coordinates,
    headBearing: blended.headBearing,
    lengthM: targetLength - Math.max(0, remaining),
    matched: true
  };
}
